import * as React from "react";
import { Badge, Text, makeStyles, tokens } from "@fluentui/react-components";
import { CheckmarkCircle20Regular, Folder20Regular, Mail20Regular } from "@fluentui/react-icons";
import type { DestinationPlatform, DriveItem, FolderItem, SiteItem } from "../types/sharepoint";

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
  },

  topRow: {
    alignItems: "center",
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    justifyContent: "space-between",
  },

  titleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },

  summaryGrid: {
    display: "grid",
    gap: tokens.spacingHorizontalS,
    gridTemplateColumns: "1fr",
  },

  summaryItem: {
    alignItems: "center",
    display: "grid",
    gap: tokens.spacingHorizontalXS,
    gridTemplateColumns: "auto 1fr",
    minWidth: 0,
  },

  mutedText: {
    color: tokens.colorNeutralForeground3,
  },

  valueText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

interface TaskpaneSummaryProps {
  selectedEmailCount: number;
  destinationPlatform: DestinationPlatform;
  selectedSite?: SiteItem;
  selectedDrive?: DriveItem;
  selectedFolder?: FolderItem;
}

function formatDestination(
  platform: DestinationPlatform,
  selectedSite?: SiteItem,
  selectedDrive?: DriveItem,
  selectedFolder?: FolderItem
): string {
  if (!selectedFolder) return "Choose a folder first";
  if (platform === "onedrive") return `OneDrive > ${selectedFolder.name}`;

  const parts = ["SharePoint", selectedSite?.displayName, selectedDrive?.name, selectedFolder.name];
  return parts.filter(Boolean).join(" > ");
}

export const TaskpaneSummary: React.FC<TaskpaneSummaryProps> = ({
  selectedEmailCount,
  destinationPlatform,
  selectedSite,
  selectedDrive,
  selectedFolder,
}) => {
  const styles = useStyles();
  const destination = formatDestination(destinationPlatform, selectedSite, selectedDrive, selectedFolder);
  const ready = selectedEmailCount > 0 && !!selectedFolder;

  return (
    <div className={styles.root}>
      <div className={styles.topRow}>
        <div className={styles.titleBlock}>
          <Text weight="semibold">Email Upload</Text>
          <Text size={200} className={styles.mutedText}>
            Review selection, choose a destination, then start the upload job.
          </Text>
        </div>
        <Badge color={ready ? "success" : "informative"} appearance="filled">
          {ready ? "Ready" : "Not ready"}
        </Badge>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryItem}>
          <Mail20Regular />
          <Text size={200} className={styles.valueText}>
            {selectedEmailCount > 0
              ? `${selectedEmailCount} email${selectedEmailCount === 1 ? "" : "s"} selected`
              : "Select one or more emails in Outlook"}
          </Text>
        </div>
        <div className={styles.summaryItem}>
          <Folder20Regular />
          <Text size={200} className={styles.valueText} title={destination}>
            Destination: {destination}
          </Text>
        </div>
        <div className={styles.summaryItem}>
          <CheckmarkCircle20Regular />
          <Text size={200} className={styles.valueText}>
            {ready ? "Ready to upload" : "Choose a folder first"}
          </Text>
        </div>
      </div>
    </div>
  );
};
